import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../components/Navbar";
import ToolSeoHero from "../components/tools-seo/ToolSeoHero";
import ToolSeoBenefits from "../components/tools-seo/ToolSeoBenefits";
import ToolSeoFAQ from "../components/tools-seo/ToolSeoFAQ";
import ToolSeoCTA from "../components/tools-seo/ToolSeoCTA";
import SeoHead from "../components/seo/SeoHead";
import StructuredData, { buildFAQSchema } from "../components/seo/StructuredData";

const THEME_KEY = "hookora_theme";

const hashtagFaqs = [
  {
    question: "What is a hashtag generator?",
    answer:
      "A hashtag generator is a tool that helps create hashtag options based on your topic or content angle. It is useful when you want faster and more organized hashtag support."
  },
  {
    question: "Who is Hookora’s Hashtag Generator for?",
    answer:
      "It is built for creators, marketers, brands, and anyone who wants a quicker way to create stronger hashtag sets for social content."
  },
  {
    question: "Can I use this for Instagram and TikTok?",
    answer:
      "Yes. Hookora is designed to support creator workflows across multiple social platforms, including hashtag use for short-form content."
  },
  {
    question: "Why do hashtags still matter?",
    answer:
      "Hashtags can help support relevance, categorization, and discoverability. They are most useful when they fit the content clearly instead of being random."
  },
  {
    question: "Does Hookora only generate hashtags?",
    answer:
      "No. Hookora also includes tools for hooks, titles, captions, descriptions, short scripts, and thumbnail text."
  },
  {
    question: "Can this help speed up posting?",
    answer:
      "Yes. One of the main benefits is reducing the time spent thinking through hashtag sets manually for every post."
  }
];

function HashtagGeneratorPage() {
  const getStoredTheme = () => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(THEME_KEY) || "light";
  };

  const [theme, setTheme] = useState(getStoredTheme());

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncTheme = () => {
      setTheme(localStorage.getItem(THEME_KEY) || "light");
    };

    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-preferences-updated", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
    };
  }, []);

  const isDark = theme === "dark";

  const pageStyle = useMemo(
    () => ({
      background: isDark ? "#020617" : "#f8fafc",
      backgroundImage: isDark
        ? "radial-gradient(circle at top left, rgba(59,130,246,0.12), transparent 28%), radial-gradient(circle at top right, rgba(124,58,237,0.10), transparent 24%)"
        : "none",
      minHeight: "100vh"
    }),
    [isDark]
  );

  return (
    <div style={pageStyle}>
      <SeoHead
        title="Hashtag Generator | Hookora"
        description="Use Hookora’s Hashtag Generator to build more relevant hashtag sets faster for social content, better discoverability, and smoother posting workflow."
        path="/hashtag-generator"
      />
      <StructuredData
        id="hashtag-generator-faq-schema"
        data={buildFAQSchema(hashtagFaqs)}
      />

      <Navbar />
      <ToolSeoHero
        badge="Hookora Tool • Hashtag Generator"
        title="Hashtag Generator for creators who want better discovery without random guesswork"
        description="The Hookora Hashtag Generator helps creators build stronger hashtag sets for social content. Instead of guessing which tags to use, you get clearer hashtag directions that support relevance, discovery, and a more organized posting workflow."
        highlights={[
          "Generate faster",
          "Improve discoverability",
          "Stay more relevant",
          "Built for creator workflow"
        ]}
        primaryCtaLabel="Start Free"
        primaryCtaTo="/register"
        secondaryCtaLabel="See Features"
        secondaryCtaTo="/features"
        previewTitle="What the Hashtag Generator helps you do"
        previewItems={[
          {
            label: "Discovery",
            text: "Create hashtag sets that give your content a better chance of being found."
          },
          {
            label: "Relevance",
            text: "Use tags that feel more connected to your topic instead of random add-ons."
          },
          {
            label: "Speed",
            text: "Move from idea to publish faster without stopping to think through every hashtag."
          }
        ]}
      />

      <ToolSeoBenefits
        eyebrow="Why creators use it"
        title="A hashtag generator should help your content feel more intentional, not more messy"
        description="Hashtags work best when they support your content clearly. Hookora helps you build cleaner hashtag sets that feel more focused, more useful, and easier to reuse across your workflow."
        benefits={[
          {
            title: "Build hashtag sets faster",
            text: "You do not need to manually brainstorm tags every time you create a post. Hookora helps you move faster."
          },
          {
            title: "Keep hashtags more relevant to your content",
            text: "Stronger hashtag choices can help your content feel more connected to the audience and topic you want to reach."
          },
          {
            title: "Support multiple platform workflows",
            text: "Whether you post on Instagram, TikTok, LinkedIn, or elsewhere, stronger hashtag support helps your process stay smoother."
          },
          {
            title: "Reduce repetitive posting friction",
            text: "When you create often, hashtag writing becomes repetitive. Hookora helps make that part easier to manage."
          },
          {
            title: "Pair hashtags with the rest of your content packaging",
            text: "Your hashtags work better when they fit the hook, caption, and message. Hookora supports that wider system."
          },
          {
            title: "Useful for creators at different stages",
            text: "Whether you are still learning content packaging or posting regularly, better hashtag support helps you publish faster."
          }
        ]}
      />

      <ToolSeoFAQ
        title="Hashtag Generator FAQ"
        intro="Here are a few common questions about Hookora’s Hashtag Generator."
        faqs={hashtagFaqs}
      />

      <ToolSeoCTA
        badge="Start free with Hookora"
        title="Use Hookora’s Hashtag Generator to build cleaner hashtag sets faster"
        description="If you want quicker hashtag support, more relevant tag sets, and a smoother creator workflow, Hookora gives you a practical place to start."
        primaryLabel="Create Free Account"
        primaryTo="/register"
        secondaryLabel="Explore Features"
        secondaryTo="/features"
      />
    </div>
  );
}

export default HashtagGeneratorPage;